import moment from "moment";

export default {
    data() {
        return {
            orderStatus: 0,
        };
    },
    computed: {
        /**
         * Status items for order status dropdown
         * @returns {Array} Array of status options
         */
        statusItems() {
            return [
                { text: this.$t("all"), value: 0 },
                { text: this.$t("users_ordered"), value: 1 },
                { text: this.$t("users_ordered_taken"), value: 4 },
                { text: this.$t("users_ordered_not_taken"), value: 3 },
                { text: this.$t("not_ordered"), value: 2 },
            ];
        },
    },
    methods: {
        /**
         * Calculates order data for a specific column (quantity or quantity_payed)
         * Groups orders by created_at date and sums the specified column
         * @param {Object} user - User object containing orders array
         * @param {string} column - Column name to calculate ('quantity' or 'quantity_payed')
         * @returns {number} Calculated total for the column
         */
        calculateOrder(user, column) {
            const orderIds = new Set((user.orders || []).map((order) => order.order_food_id));
            return [...orderIds]
                .map((orderFoodId) => {
                    const orders = (user.orders || [])
                        .filter((order) => order.order_food_id === orderFoodId)
                        .sort((a, b) => b[column] - a[column]);
                    if (!orders.length) return;
                    return orders[0][column];
                })
                .filter(Boolean)
                .reduce((acc, curr) => acc + curr, 0);
        },

        /**
         * Processes users array to calculate order_count, order_payed, order_date, and difference
         * Modifies the original user objects with calculated values
         * @param {Array} users - Array of user objects to process
         * @returns {Array} Processed users array with calculated order data
         */
        processUsersOrderData(users) {
            return (users || []).map((user) => {
                const orderCount = this.calculateOrder(user, "quantity");
                const orderPayedCount = this.calculateOrder(user, "quantity_payed");

                // Set calculated values
                user.order_count = orderCount || 0;
                user.order_payed = orderPayedCount || 0;
                user.difference = user.order_count - user.order_payed;

                // Set order_date from first order if orders exist
                if ((user.orders || []).length > 0) {
                    user.order_date = moment(user.orders[0].created_at).format("DD.MM.YYYY HH:mm");
                }

                return user;
            });
        },

        /**
         * Filters users based on order status
         * @param {Array} users - Array of processed user objects
         * @param {number} status - Status filter value (0=all, 1=ordered, 2=not_ordered, 3=ordered_not_taken)
         * @returns {Array} Filtered users array
         */
        filterUsersByStatus(users, status) {
            if (!status) return users;

            return users.filter((user) => {
                switch (status) {
                    case 1: // users_ordered
                        return user.order_count > 0;
                    case 2: // not_ordered
                        return user.order_count === 0;
                    case 3: // users_ordered_not_taken
                        return user.order_count > 0 && user.order_payed === 0;
                    case 4: // users_ordered_taken
                        return user.order_count > 0 && user.order_payed > 0;
                    default:
                        return true;
                }
            });
        },

        /**
         * Calculates totals for all users across different metrics
         * @param {Array} users - Array of processed user objects
         * @returns {Object} Object containing calculated totals
         */
        calculateUserTotals(users) {
            const totals = {
                order_count: 0,
                order_payed: 0,
                meals: 0,
                shift_one: 0,
                shift_two: 0,
                shift_three: 0
            };

            users.forEach((user) => {
                totals.order_count += user.order_count || 0;
                totals.order_payed += user.order_payed || 0;
                totals.meals += user.meals || 0;
                totals.shift_one += user.shift === 1 ? user.order_count || 0 : 0;
                totals.shift_two += user.shift === 2 ? user.order_count || 0 : 0;
                totals.shift_three += user.shift === 3 ? user.order_count || 0 : 0;
            });

            return totals;
        }
    }
};
